// F16 — the contact page, reduced to its one job: booking a demo.
//
// This page carries the only working form in the landing suite. The submit
// handler, the endpoint (`POST /api/contact/submit`), the payload shape and
// every field `name` are unchanged — this pass was presentation and copy only.
//
// What was deleted here, and why:
//
// - The three "response within N hours" promises (24 hours for demos, 4 hours
//   for sales, 2 hours for support). Nobody is rostered to meet them. They are
//   replaced by a single modest line: we usually reply within one working day.
// - The premium-tier round-the-clock emergency support promise, and the
//   technical-support hours block it sat in. There is no out-of-hours rota,
//   and there is no premium tier to sell it with.
// - "Join hundreds of restaurants already transforming their operations."
//   The business is pre-pilot; it has no customers. Same reason the
//   "Existing Customer? Log In" button went.
// - "Free setup assistance and staff training included" and "Ongoing support
//   to ensure your success" — commitments nobody has agreed to honour.
// - The Live Chat card. Its "Start Chat" button had no handler at all, so the
//   advertised "instant support" was a button that did nothing.
// - The enquiry-type, restaurant-type and current-POS fields. The page now
//   asks for the five things needed to book a demo; anything else the visitor
//   wants to say goes in the message box. The keys stay in the posted body so
//   the backend contract is untouched.
//
// The form's own validation is presentation-layer only: it runs before
// handleFormSubmit and never changes what is posted.
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import LandingLayout from '../../components/landing/LandingLayout';
import { useLanguage } from '../../context/LanguageContext';
import { C, A, FONT, TYPE, SP, R, W, S } from '../../components/landing/tokens';
import ArchMark from '../../components/landing/ArchMark';
import { COUNTRIES } from '../../components/landing/countries';
import api from '../../services/api';

// auto-fit grids stack on their own below the min track, so the two-column
// rows need no media query and mirror cleanly in RTL.
const grid = (min) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${min}px), 1fr))`,
    gap: SP.xl,
});

// ---- form styling ---------------------------------------------------------
// These live at module scope deliberately. A component declared inside the
// page body is a new type on every render, which would unmount and remount
// each input and drop the caret after a single keystroke.

const control = {
    background: C.surface,
    border: `1px solid ${C.lineStrong}`,
    borderRadius: R.sm,
    padding: '12px 14px',
    ...TYPE.body,
    color: C.ink,
    width: '100%',
    boxSizing: 'border-box',
    fontFamily: FONT.body,
};

const focusOn = (e) => {
    e.currentTarget.style.borderColor = C.ink;
    e.currentTarget.style.outline = `2px solid ${A.inkRing}`;
    e.currentTarget.style.outlineOffset = '1px';
};

const focusOff = (invalid) => (e) => {
    e.currentTarget.style.borderColor = invalid ? C.danger : C.lineStrong;
    e.currentTarget.style.outline = 'none';
};

// A labelled control plus its error message, wired for screen readers.
const Field = ({ id, label, error, children }) => (
    <div>
        <label htmlFor={id} style={S.label}>{label}</label>
        {children}
        {error && (
            <p
                id={`${id}-error`}
                role="alert"
                style={{ ...TYPE.caption, color: C.danger, marginBlock: `${SP.sm}px 0` }}
            >
                {error}
            </p>
        )}
    </div>
);

const MarkedItem = ({ children }) => (
    <li style={{ display: 'flex', alignItems: 'flex-start', gap: SP.md }}>
        <span style={{ marginBlockStart: 3 }}>
            <ArchMark size={15} color={C.inkFaint} />
        </span>
        <span style={{ ...TYPE.body, color: C.inkMuted }}>{children}</span>
    </li>
);

const ContactUsPage = () => {
    const { t } = useLanguage();

    const [submitting, setSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});

    // The posted body keeps every key the backend model declares. The three
    // fields no longer asked for are sent as they always were; enquiryType is
    // fixed to a demo request because that is now the only thing this page does.
    const [formData, setFormData] = useState({
        restaurantName: '',
        contactName: '',
        email: '',
        phone: '',
        restaurantType: '',
        currentPOS: '',
        message: '',
        enquiryType: 'request-a-demo',
        country: ''
    });

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    // Auto-detect country based on IP
    useEffect(() => {
        const detectCountry = async () => {
            try {
                const response = await fetch('https://ipapi.co/json/');
                const data = await response.json();
                // Only accept a detected name that actually exists in the
                // list. ipapi spells some countries differently (e.g. "United
                // States of America"), and a value with no matching <option>
                // makes the select display the wrong country while posting
                // the unmatched string.
                const detected = COUNTRIES.includes(data.country_name) ? data.country_name : '';
                setFormData(prev => ({
                    ...prev,
                    country: detected
                }));
            } catch (error) {
                console.log('Could not detect country:', error);
                // The old fallback took timezone.split('/')[0], which for
                // "Europe/London" yields "Europe" — not a country and not in
                // COUNTRIES. The select then matched no option and displayed
                // the first enabled one (Afghanistan) while the form quietly
                // posted "Europe". Better to detect nothing and let the
                // placeholder stand, so the visitor picks their own country.
                console.log('Leaving country unset for the visitor to choose.');
            }
        };

        detectCountry();
    }, []);

    // Reset the fields once a submission lands. The confirmation itself stays
    // on screen — it used to disappear after five seconds, which meant a
    // visitor who looked away came back to an empty form and no receipt.
    useEffect(() => {
        if (submitSuccess) {
            setFormData({
                restaurantName: '',
                contactName: '',
                email: '',
                phone: '',
                restaurantType: '',
                currentPOS: '',
                message: '',
                enquiryType: 'request-a-demo',
                country: formData.country
            });
        }
    }, [submitSuccess, formData.country]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setSubmitError('');

        try {
            await api.post('/api/contact/submit', formData);

            setSubmitSuccess(true);
            setSubmitting(false);
        } catch (error) {
            console.error('Form submission error:', error);
            setSubmitError(t('Failed to submit form. Please try again or email us directly at admin@aqana.uk'));
            setSubmitting(false);
        }
    };

    // Runs before the handler above. Returns true when the form may be posted.
    const validate = () => {
        const next = {};
        if (!formData.contactName.trim()) {
            next.contactName = t('Please enter your name.');
        }
        if (!formData.restaurantName.trim()) {
            next.restaurantName = t('Please enter the name of your venue.');
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
            next.email = t('Please enter an email address we can reply to.');
        }
        if (!formData.phone.trim()) {
            next.phone = t('Please enter a phone number.');
        }
        if (!formData.country) {
            next.country = t('Please choose your country.');
        }
        setFieldErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleValidatedSubmit = (e) => {
        if (!validate()) {
            e.preventDefault();
            return;
        }
        handleFormSubmit(e);
    };

    const invalidProps = (id) => (fieldErrors[id]
        ? { 'aria-invalid': 'true', 'aria-describedby': `${id}-error` }
        : {});

    const controlFor = (id) => ({
        ...control,
        borderColor: fieldErrors[id] ? C.danger : C.lineStrong,
    });

    const nextSteps = [
        t('We read what you sent and check that Aqana suits your venue.'),
        t('We load your menu and prices into a demo account.'),
        t('We walk you through a full service, on a call or in person.'),
    ];

    return (
        <LandingLayout>
            {/* ---------- 1. Hero ---------- */}
            <section style={S.section()}>
                <div style={S.container}>
                    <div style={{ maxWidth: W.prose }}>
                        <h1 style={{ ...TYPE.displayXl, color: C.ink, margin: 0, textWrap: 'balance' }}>
                            {t('Get a demo')}
                        </h1>
                        <p style={{ ...TYPE.bodyL, color: C.inkMuted, marginBlock: `${SP.lg}px ${SP.md}px` }}>
                            {t('Send us a few details and we will set Aqana up with your own menu, then walk you through a service.')}
                        </p>
                        <p style={{ ...TYPE.caption, color: C.inkFaint, margin: 0 }}>
                            {t('We usually reply within one working day.')}
                        </p>
                    </div>
                </div>
            </section>

            {/* ---------- 2. The form ---------- */}
            <section id="contact-form" style={S.section({ deep: true })}>
                <div style={{ ...S.container, ...grid(380), gap: SP['3xl'], alignItems: 'start' }}>

                    {/* --- the form itself, or its confirmation --- */}
                    {submitSuccess ? (
                        <div style={{ ...S.card, textAlign: 'center' }}>
                            <ArchMark size={48} />
                            <h2 style={{ ...TYPE.title, color: C.ink, marginBlock: `${SP.lg}px ${SP.sm}px` }}>
                                {t('Thank you, we have your details.')}
                            </h2>
                            <p style={{ ...TYPE.body, color: C.inkMuted, margin: 0 }}>
                                {t('We will read what you sent and reply by email, usually within one working day.')}
                            </p>
                        </div>
                    ) : (
                        <div style={S.card}>
                            <h2 style={{ ...TYPE.displayM, color: C.ink, margin: 0, textWrap: 'balance' }}>
                                {t('Tell us about your venue')}
                            </h2>
                            <p style={{ ...TYPE.body, color: C.inkMuted, marginBlock: `${SP.md}px ${SP.xl}px` }}>
                                {t('Five details is all we need to get started.')}
                            </p>

                            {submitError && (
                                <div
                                    role="alert"
                                    style={{
                                        border: `1px solid ${C.danger}`,
                                        borderRadius: R.sm,
                                        padding: SP.lg,
                                        marginBlockEnd: SP.xl,
                                        ...TYPE.caption,
                                        color: C.danger,
                                    }}
                                >
                                    {submitError}
                                </div>
                            )}

                            <form
                                noValidate
                                onSubmit={handleValidatedSubmit}
                                style={{ display: 'flex', flexDirection: 'column', gap: SP.xl }}
                            >
                                <div style={grid(200)}>
                                    <Field id="contactName" label={t('Your name')} error={fieldErrors.contactName}>
                                        <input
                                            id="contactName"
                                            type="text"
                                            name="contactName"
                                            value={formData.contactName}
                                            onChange={handleInputChange}
                                            onFocus={focusOn}
                                            onBlur={focusOff(!!fieldErrors.contactName)}
                                            required
                                            placeholder=""
                                            style={controlFor('contactName')}
                                            {...invalidProps('contactName')}
                                        />
                                    </Field>

                                    <Field id="restaurantName" label={t('Venue name')} error={fieldErrors.restaurantName}>
                                        <input
                                            id="restaurantName"
                                            type="text"
                                            name="restaurantName"
                                            value={formData.restaurantName}
                                            onChange={handleInputChange}
                                            onFocus={focusOn}
                                            onBlur={focusOff(!!fieldErrors.restaurantName)}
                                            required
                                            placeholder={t('The name above the door')}
                                            style={controlFor('restaurantName')}
                                            {...invalidProps('restaurantName')}
                                        />
                                    </Field>
                                </div>

                                <Field id="email" label={t('Email')} error={fieldErrors.email}>
                                    <input
                                        id="email"
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        onFocus={focusOn}
                                        onBlur={focusOff(!!fieldErrors.email)}
                                        required
                                        placeholder={t('you@yourvenue.co.uk')}
                                        style={controlFor('email')}
                                        {...invalidProps('email')}
                                    />
                                </Field>

                                {/* Country sits beside the phone number so the dialling
                                    context is obvious. Both mirror in Arabic. */}
                                <div style={grid(200)}>
                                    <Field id="country" label={t('Country')} error={fieldErrors.country}>
                                        <select
                                            id="country"
                                            name="country"
                                            value={formData.country}
                                            onChange={handleInputChange}
                                            onFocus={focusOn}
                                            onBlur={focusOff(!!fieldErrors.country)}
                                            required
                                            style={controlFor('country')}
                                            {...invalidProps('country')}
                                        >
                                            <option value="" disabled>{t('Select your country')}</option>
                                            {COUNTRIES.map((country) => (
                                                <option key={country} value={country}>{country}</option>
                                            ))}
                                        </select>
                                    </Field>

                                    <Field id="phone" label={t('Phone number')} error={fieldErrors.phone}>
                                        <input
                                            id="phone"
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            onFocus={focusOn}
                                            onBlur={focusOff(!!fieldErrors.phone)}
                                            required
                                            placeholder={t('+44 7700 900000')}
                                            style={controlFor('phone')}
                                            {...invalidProps('phone')}
                                        />
                                    </Field>
                                </div>

                                <Field id="message" label={t('Tell us about your venue (optional)')}>
                                    <textarea
                                        id="message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        onFocus={focusOn}
                                        onBlur={focusOff(false)}
                                        rows={4}
                                        placeholder={t('How many tills, how many staff, and what you use to take orders today.')}
                                        style={{ ...control, resize: 'vertical' }}
                                    />
                                </Field>

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    style={{
                                        ...S.btnPrimary,
                                        width: '100%',
                                        opacity: submitting ? 0.5 : 1,
                                        cursor: submitting ? 'not-allowed' : 'pointer',
                                    }}
                                >
                                    {submitting ? t('Sending…') : t('Request a demo')}
                                </button>

                                <p style={{ ...TYPE.caption, color: C.inkFaint, margin: 0, textAlign: 'center' }}>
                                    {t('We use your details to arrange the demo and nothing else.')}
                                </p>
                            </form>
                        </div>
                    )}

                    {/* --- direct contact, alongside the form --- */}
                    <div>
                        <span style={S.label}>{t('Or reach us directly')}</span>
                        <p style={{ ...TYPE.body, color: C.inkMuted, marginBlock: `0 ${SP.sm}px` }}>
                            {t('Email us and the same person reads it.')}
                        </p>
                        <a
                            href="mailto:admin@aqana.uk"
                            style={{
                                ...TYPE.bodyL,
                                color: C.ink,
                                textDecoration: 'none',
                                borderBlockEnd: `1px solid ${C.lineStrong}`,
                            }}
                        >
                            admin@aqana.uk
                        </a>

                        <p style={{ ...TYPE.caption, color: C.inkFaint, marginBlock: `${SP.lg}px 0` }}>
                            {t('Monday to Friday, 9:00 to 18:00 UK time.')}
                        </p>

                        <div style={{ borderBlockStart: `1px solid ${C.line}`, marginBlockStart: SP['2xl'], paddingBlockStart: SP['2xl'] }}>
                            <h2 style={{ ...TYPE.title, color: C.ink, margin: 0 }}>
                                {t('What happens next')}
                            </h2>
                            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: SP.md, marginBlockStart: SP.lg }}>
                                {nextSteps.map((step) => <MarkedItem key={step}>{step}</MarkedItem>)}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* ---------- 3. Questions first ---------- */}
            <section
                style={{
                    ...S.section({ tight: true }),
                    background: C.ink,
                    color: C.paper,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <ArchMark
                    size={340}
                    color={C.paper}
                    opacity={0.05}
                    style={{ position: 'absolute', insetInlineEnd: -50, insetBlockStart: -30, pointerEvents: 'none' }}
                />
                <div style={{ ...S.container, position: 'relative', textAlign: 'center' }}>
                    <h2 style={{ ...TYPE.displayL, color: C.paper, margin: 0, textWrap: 'balance' }}>
                        {t('Questions before you book?')}
                    </h2>
                    <p style={{ ...TYPE.bodyL, color: A.paperMuted, maxWidth: 520, marginInline: 'auto', marginBlock: `${SP.lg}px ${SP['2xl']}px` }}>
                        {t('The FAQ page answers the common questions about pricing, hardware and setup.')}
                    </p>
                    <Link
                        to="/faq"
                        style={{ ...S.btnPrimary, background: C.paper, color: C.ink, border: `1px solid ${C.paper}` }}
                    >
                        {t('Read the FAQ')}
                    </Link>
                </div>
            </section>
        </LandingLayout>
    );
};

export default ContactUsPage;
